import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/header';
import Footer from '../../../components/footer';
import SEO from '../../../components/SEO';
import '../../artikler/styles/artikler-clean.css';
import './mobilvenlige-hjemmesider.css';

const MobilvenligeHjemmesider = () => {
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  // Beregn læse fremskridt og aktiv sektion
  useEffect(() => {
    const updateReadingProgress = () => {
      const article = document.querySelector('.artikel-content');
      if (article) {
        const scrollTop = window.scrollY;
        const docHeight = article.offsetHeight;
        const winHeight = window.innerHeight;
        const scrollPercent = scrollTop / (docHeight - winHeight);
        const scrollPercentRounded = Math.round(scrollPercent * 100);
        setReadingProgress(scrollPercentRounded > 100 ? 100 : Math.max(0, scrollPercentRounded));
      }
    };

    const updateActiveSection = () => {
      const sections = document.querySelectorAll('.artikel-section[id]');
      const scrollTop = window.scrollY + 200; // Adjust for header
      
      let activeSection = '';
      
      // Find den sidste sektion der er passeret
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop <= scrollTop) {
          activeSection = section.id;
          break;
        }
      }
      
      setActiveSection(activeSection);
    };

    const handleScroll = () => {
      updateReadingProgress();
      updateActiveSection();
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Beregn den rigtige scroll position
      const headerHeight = 112; // Fixed header højde
      const readingProgressHeight = 2; // Reading progress bar højde
      const extraPadding = 30; // Ekstra rum så overskriften er tydeligt synlig
      const totalOffset = headerHeight + readingProgressHeight + extraPadding; // Total 144px
      
      // Få elementets position relativt til viewport
      const rect = element.getBoundingClientRect();
      const elementTop = window.pageYOffset + rect.top;
      
      // Scroll til position minus offset
      const scrollToPosition = elementTop - totalOffset;
      
      window.scrollTo({
        top: Math.max(0, scrollToPosition),
        behavior: 'smooth'
      });
    }
  };

  const tableOfContents = [
    { id: 'mobile-realitet', title: 'Mobiltelefoner har overtaget' },
    { id: 'mobile-vs-desktop', title: 'Mobil kontra computer' },
    { id: 'google-mobile-first', title: 'Google mobil-først indeksering' },
    { id: 'hastighed', title: 'Hastighed på mobil' },
    { id: 'brugeroplevelse', title: 'Mobilbrugeroplevelse' },
    { id: 'mobile-seo', title: 'Mobil SEO og webdesign trends' },
    { id: 'case-study', title: 'Case study' },
    { id: 'design-principper', title: 'Design principper' },
    { id: 'test-tools', title: 'Test værktøjer' },
    { id: 'handlingsplan', title: 'Handlingsplan' },
    { id: 'fremtiden', title: 'Fremtiden med mobiltelefoner' },
    { id: 'konklusion', title: 'Konklusion' }
  ];

  return (
    <div className="artikel-side">
      <SEO 
        title="Mobilvenlige Hjemmesider 2025 - Komplet Guide til Mobile-First Design | Parla"
        description="Over 65% af web-trafik kommer fra mobile enheder. Lær hvorfor din hjemmeside SKAL være mobile-first i 2025. Komplet guide med tips og værktøjer."
        keywords="mobilvenlige hjemmesider, responsive design, mobile-first, mobile SEO, Google mobile, brugeroplevelse"
        url="https://parlabranding.dk/artikler/mobilvenlige-hjemmesider"
        canonicalUrl="https://www.parlabranding.dk/artikler/mobilvenlige-hjemmesider"
        type="article"
      />
      
      <Header />
      
      {/* Reading Progress Bar */}
      <div className="reading-progress">
        <div 
          className="reading-progress-bar"
          style={{ width: `${readingProgress}%` }}
        ></div>
      </div>

      {/* Hero Section */}
      <section className="artikel-hero">
        <div className="container">
          <div className="artikel-hero-content">
            <div className="hero-content-left">
              <div className="artikel-meta">
                <span className="artikel-kategori">Mobile & UX</span>
                <span className="artikel-dato">30. september 2025</span>
                <span className="artikel-læsetid">15 min læsning</span>
              </div>
              
              <h1 className="artikel-title">
                Mobilvenlige Hjemmesider 2025: Hvorfor Din Virksomhed SKAL Være Mobil-først
              </h1>
              
              <p className="artikel-subtitle">
                Over 65% af al webtrafik kommer nu fra mobiltelefoner og tablets. Hvis din hjemmeside ikke følger webdesign trends 2025 
                med mobiloptimering, mister du bogstaveligt talt kunder hver dag. Her er den komplette guide til at gøre din hjemmeside mobil-klar.
              </p>
              
              <div className="artikel-author">
                <div className="author-avatar">PB</div>
                <div className="author-info">
                  <strong>Skrevet af Parla Branding</strong>
                  <span>Mobil- og brugeroplevelse eksperter</span>
                </div>
              </div>
            </div>

            <div className="hero-content-right">
              <div className="hero-image-container">
                <img 
                  src="https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=800&h=500&fit=crop"
                  alt="Person bruger smartphone til at browse hjemmeside"
                  className="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="artikel-content">
        <div className="container">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <ol className="breadcrumb-list">
              <li className="breadcrumb-item">
                <Link to="/">Hjem</Link>
              </li>
              <li className="breadcrumb-separator">›</li>
              <li className="breadcrumb-item">
                <Link to="/artikler">Artikler</Link>
              </li>
              <li className="breadcrumb-separator">›</li>
              <li className="breadcrumb-item current" aria-current="page">
                Mobilvenlige Hjemmesider
              </li>
            </ol>
          </nav>

          <div className="artikel-layout">
            
            {/* Left Sidebar - Table of Contents */}
            <aside className="artikel-toc-sidebar">
              <h3>Indholdsfortegnelse</h3>
              <ul className="toc-list">
                {tableOfContents.map((item, index) => (
                  <li key={index}>
                    <button
                      className={activeSection === item.id ? 'active' : ''}
                      onClick={() => scrollToSection(item.id)}
                    >
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Main Content */}
            <main className="artikel-main">

              {/* Mobile Stats */}
              <section id="mobile-realitet" className="artikel-section">
        <h2>Realiteten: Mobiltelefoner Har Overtaget Webdesign</h2>
        <p>
          Vi befinder os i en verden hvor smartphones og tablets ikke længere er "rart at have" - de er blevet det primære vindue 
          til internettet for de fleste danskere. Dette er en af de største webdesign trends 2025.
        </p>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">65%</div>
            <div className="stat-label">af webtrafik er fra mobil</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">3 sek</div>
            <div className="stat-label">max loading tid før brugere forlader</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">61%</div>
            <div className="stat-label">af Google søgninger er fra mobil</div>
          </div>
        </div>
      </section>

              {/* Image Break */}
              <div className="artikel-image-break">
        <img 
          src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&h=600&fit=crop" 
          alt="Mobiltelefoner og tablets viser forskellige responsive hjemmesider"
          className="content-image"
        />
                <div className="image-caption">
                  Mobiltelefoner og tablets i alle størrelser kræver responsivt webdesign for optimal brugeroplevelse - en vigtig webdesign trend 2025
                </div>
              </div>

              {/* Mobile vs Desktop Comparison */}
              <section id="mobile-vs-desktop" className="artikel-section">
        <h2>Mobil Kontra Computer: Kampen Er Afgjort</h2>
        <p>
          Statistikkerne taler deres tydelige sprog. Mobiltelefoner og tablets har ikke kun "overtaget" - de dominerer fuldstændigt 
          og former webdesign trends 2025.
        </p>

        <div className="comparison-box">
          <h3>Webtrafik Fordeling 2025</h3>
          <div className="comparison-stats">
            <div className="comparison-item">
              <span className="device-label">Mobil</span>
              <span className="device-percentage primary">65%</span>
            </div>
            <div className="comparison-item">
              <span className="device-label">Computer</span>
              <span className="device-percentage">32%</span>
            </div>
            <div className="comparison-item">
              <span className="device-label">Tablet</span>
              <span className="device-percentage">3%</span>
            </div>
          </div>
        </div>
      </section>

              {/* Google Mobile-First */}
              <section id="google-mobile-first" className="artikel-section">
        <h2>Google Straffer Ikke-Mobilvenlige Hjemmesider</h2>
        <p>
          Google har siden 2018 implementeret "mobil-først indeksering" - hvilket betyder at Google primært bruger 
          mobilversionen af din hjemmeside til at rangere den i søgeresultater. Dette er essentielt for webdesign trends 2025.
        </p>

        <div className="highlight-box warning">
          <h3>Google Mobil-Først Indeksering</h3>
          <ul>
            <li>Google indekserer primært mobilversioner</li>
            <li>Ikke-mobilvenlige hjemmesider rangerer lavere</li>
            <li>Mobilvenlighed er en vigtig ranking-faktor</li>
            <li>Core Web Vitals måles på mobilenheder</li>
          </ul>
        </div>
      </section>

              {/* Loading Speed Impact */}
              <section id="hastighed" className="artikel-section">
        <h2>Hastighed Afgør Alt På Mobil - Webdesign Trends 2025</h2>
        <p>
          På smartphones og tablets er internetforbindelsen ofte langsommere, og brugerne er mere utålmodige. 
          Hastighedsoptimering er derfor kritisk for moderne webdesign.
        </p>

        <div className="speed-chart">
          <h3>Loading tid påvirker bounce rate</h3>
          <div className="speed-grid">
            <div className="speed-item">
              <div className="speed-time">1 sek</div>
              <div className="speed-bounce">10% bounce rate</div>
            </div>
            <div className="speed-item">
              <div className="speed-time">3 sek</div>
              <div className="speed-bounce">32% bounce rate</div>
            </div>
            <div className="speed-item">
              <div className="speed-time">5 sek</div>
              <div className="speed-bounce">90% bounce rate</div>
            </div>
            <div className="speed-item">
              <div className="speed-time">10 sek</div>
              <div className="speed-bounce">123% øgning</div>
            </div>
          </div>
        </div>
      </section>

              {/* User Experience */}
              <section id="brugeroplevelse" className="artikel-section">
        <h2>Brugeroplevelsen På Mobil - Vigtig Webdesign Trend</h2>
        <p>
          Mobilbrugere har fundamentalt forskellige behov og forventninger end computerbrugere. 
          De er ofte på farten og har brug for hurtig adgang til information.
        </p>

        <div className="ux-comparison">
          <div className="ux-column bad">
            <h3>Dårlig Mobilbrugeroplevelse</h3>
            <ul>
              <li>Tekst for lille til at læse</li>
              <li>Links for tæt på hinanden</li>
              <li>Horisontalt scrolling påkrævet</li>
              <li>Pop-ups der ikke kan lukkes</li>
              <li>Langsom loading tid</li>
            </ul>
            <p><small>Disse er klassiske <Link to="/artikler/webdesign-fejl-virksomheder-laver">webdesign fejl</Link> som mange virksomheder stadig laver.</small></p>
          </div>
          
          <div className="ux-column good">
            <h3>God Mobilbrugeroplevelse</h3>
            <ul>
              <li>Læsbar tekst uden zoom</li>
              <li>Touch-venlige knapper</li>
              <li>Responsivt design</li>
              <li>Mobiloptimeret navigation</li>
              <li>Hurtig loading (under 3 sek)</li>
            </ul>
          </div>
        </div>
      </section>

              {/* Image Break 2 */}
              <div className="artikel-image-break">
        <img 
          src="https://images.unsplash.com/photo-1563203369-26f2e4a5ccf7?w=1200&h=600&fit=crop" 
          alt="Responsive web design på forskellige skærmstørrelser"
          className="content-image"
        />
                <div className="image-caption">
                  Responsive design sikrer optimal visning på alle enheder og skærmstørrelser
                </div>
              </div>

              {/* Mobile SEO */}
              <section id="mobile-seo" className="artikel-section">
        <h2>Mobil SEO: Din Ranking Afhænger Af Det - Webdesign Trends 2025</h2>
        <p>
          Mobilvenlighed er ikke kun en brugeroplevelse - det er en direkte ranking-faktor i Google. 
          Hjemmesider der ikke er mobiloptimeret vil systematisk rangere dårligere i søgeresultaterne. 
          Læs mere om moderne SEO-strategier i vores <Link to="/artikler/seo-guide-2025">komplette SEO guide 2025</Link>.
        </p>

        <div className="seo-factors">
          <div className="seo-factor">
            <h3>Mobilvenlighedstest</h3>
            <p>Google tester om din hjemmeside er mobilvenlig og bruger det som ranking-signal</p>
          </div>
          <div className="seo-factor">
            <h3>Core Web Vitals</h3>
            <p>Hastighed, interaktivitet og visuel stabilitet måles på mobilenheder</p>
          </div>
          <div className="seo-factor">
            <h3>Brugeroplevelse Signaler</h3>
            <p>Afvisningsprocent, tid på hjemmeside og klik-frekvens fra mobilbrugere</p>
          </div>
        </div>
      </section>

              {/* Case Study */}
              <section id="case-study" className="artikel-section">
        <div className="case-study">
          <h2>Case Study: Mobile Optimization Resultater</h2>
          <div className="case-info">
            <p><strong>Virksomhed:</strong> Lokal detailhandel</p>
            <p><strong>Udfordring:</strong> Høj afvisningsprocent på mobilenheder (78%)</p>
            <p><strong>Løsning:</strong> Komplet mobilredesign og hastighedsoptimering følgende webdesign trends 2025 
            (se <Link to="/artikler/hvad-koster-hjemmesider">hvad et mobilredesign koster</Link>)</p>
          </div>
          
          <div className="case-results">
            <div className="result-item">
              <div className="result-number green">-45%</div>
              <div className="result-label">Afvisningsprocent reduktion</div>
            </div>
            <div className="result-item">
              <div className="result-number green">+127%</div>
              <div className="result-label">Stigning i mobilkonverteringer</div>
            </div>
            <div className="result-item">
              <div className="result-number green">+89%</div>
              <div className="result-label">Forbedring i Google ranking</div>
            </div>
          </div>
        </div>
      </section>

              {/* Mobile Design Principles */}
              <section id="design-principper" className="artikel-section">
        <h2>Mobil-Først Design Principper - Webdesign Trends 2025</h2>
        <p>
          At designe mobil-først betyder at starte med den mindste skærm og bygge op derfra. 
          Dette sikrer at alle funktioner virker optimalt på smartphones og tablets og følger 
          <Link to="/artikler/webdesign-trends">moderne webdesign trends 2025</Link>.
        </p>

        <div className="design-principles">
          <div className="principle">
            <div className="principle-number">1</div>
            <div className="principle-content">
              <h3>Touch-Først Brugerflade</h3>
              <p>Design for fingre, ikke computermús. Knapper skal være mindst 44px høje og have nok plads omkring sig.</p>
            </div>
          </div>
          <div className="principle">
            <div className="principle-number">2</div>
            <div className="principle-content">
              <h3>Forenklet Navigation</h3>
              <p>Burgermenu, prioriteret indhold og maksimalt 7 hovedkategorier i navigationen.</p>
            </div>
          </div>
          <div className="principle">
            <div className="principle-number">3</div>
            <div className="principle-content">
              <h3>Optimeret Indhold</h3>
              <p>Kortere afsnit, større skrift (minimum 16px) og tydelig hierarki med overskrifter. 
              <Link to="/artikler/minimalistisk-webdesign">Minimalistisk webdesign</Link> fungerer særligt godt til mobilenheder.</p>
            </div>
          </div>
          <div className="principle">
            <div className="principle-number">4</div>
            <div className="principle-content">
              <h3>Fokus På Ydeevne</h3>
              <p>Optimerede billeder, minimal JavaScript og server-side rendering for hurtig indlæsning.</p>
            </div>
          </div>
        </div>
      </section>

              {/* Testing Tools */}
              <section id="test-tools" className="artikel-section">
        <h2>Test Din Hjemmesides Mobilydeevne - Webdesign Trends 2025</h2>
        <p>
          Der findes flere gratis værktøjer til at teste og forbedre din hjemmesides mobilydeevne. 
          Her er de vigtigste værktøjer for moderne webdesign:
        </p>

        <div className="tools-grid">
          <div className="tool-card">
            <h3>Google Mobilvenlighedstest</h3>
            <p>Test om Google anser din hjemmeside som mobilvenlig</p>
            <div className="tool-link">search.google.com/test/mobile-friendly</div>
          </div>
          <div className="tool-card">
            <h3>PageSpeed Insights</h3>
            <p>Analyser hastighed og Core Web Vitals på både mobil og computer</p>
            <div className="tool-link">pagespeed.web.dev</div>
          </div>
          <div className="tool-card">
            <h3>GTmetrix</h3>
            <p>Detaljeret analyse af loading hastighed og optimeringsforslag</p>
            <div className="tool-link">gtmetrix.com</div>
          </div>
        </div>
      </section>

              {/* Action Plan */}
              <section id="handlingsplan" className="artikel-section">
        <h2>Din Mobilhandlingsplan - Webdesign Trends 2025</h2>
        <p>
          Her er en trinvis guide til at gøre din hjemmeside mobilklar følgende moderne webdesign trends. Start med punkt 1 og arbejd dig gennem listen.
        </p>

        <div className="action-steps">
          <div className="step immediate">
            <div className="step-header">
              <span className="step-number">1</span>
              <h3>Øjeblikkelig handling</h3>
            </div>
            <ul>
              <li>Test din hjemmeside med Google Mobilvenlighedstest</li>
              <li>Analyser hastighed med PageSpeed Insights</li>
              <li>Tjek din hjemmeside på din egen smartphone</li>
              <li>Identificer de største problemer</li>
            </ul>
          </div>

          <div className="step short-term">
            <div className="step-header">
              <span className="step-number">2</span>
              <h3>Næste 2 uger</h3>
            </div>
            <ul>
              <li>Implementer responsivt design hvis ikke allerede gjort</li>
              <li>Optimer billeder for hurtigere indlæsning</li>
              <li>Test navigation og brugeroplevelse på mobil</li>
              <li>Fjern pop-ups der blokerer mobilbrugere</li>
            </ul>
          </div>

          <div className="step long-term">
            <div className="step-header">
              <span className="step-number">3</span>
              <h3>Næste måned</h3>
            </div>
            <ul>
              <li>Implementer mobil-først design principper</li>
              <li>Optimer Core Web Vitals resultater</li>
              <li>A/B test forskellige mobillayouts</li>
              <li>Overvåg mobilanalytics løbende</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Future of Mobile */}
      <section id="fremtiden" className="artikel-section">
        <h2>Fremtiden Er Endnu Mere Mobilorienteret - Webdesign Trends 2025</h2>
        <p>
          Tendenserne peger på at smartphones og tablets vil blive endnu mere dominerende. 
          Nye teknologier som 5G, foldbare skærme og stemmesøgning vil kræve yderligere tilpasninger i webdesign. 
          Læs om hvordan <Link to="/artikler/verden-med-ai">AI påvirker fremtidens hjemmesider</Link>.
        </p>

        <div className="future-trends">
          <div className="trend">
            <h3>2025-2026: Progressive Web Apps (PWA)</h3>
            <p>Hjemmesider der fungerer som native apps med offline funktionalitet</p>
          </div>
          <div className="trend">
            <h3>2026-2027: Stemme-Først Brugerflader</h3>
            <p>Integration af stemmesøgning og kommandoer i mobilweb oplevelser</p>
          </div>
          <div className="trend">
            <h3>2027+: AR/VR Integration</h3>
            <p>Mobilenheder som adgang til forstærket virkelighed oplevelser</p>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section id="konklusion" className="artikel-section">
        <h2>Konklusion: Mobiltelefoner Er Ikke Fremtiden - Det Er Nu</h2>
        <p>
          Mobilvenlige hjemmesider er ikke længere et "rart at have" - det er en absolut nødvendighed for enhver 
          virksomhed der vil overleve online. Med over 65% af al webtrafik fra smartphones og tablets og Googles 
          mobil-først indeksering, er der simpelthen ingen undskyldning for ikke at have en mobiloptimeret hjemmeside der følger webdesign trends 2025.
        </p>
        
        <div className="highlight-box">
          <h3>Husk dette om webdesign trends 2025</h3>
          <ul>
            <li>Mobilbrugere forventer hurtig indlæsning og nem navigation</li>
            <li>Google straffer ikke-mobilvenlige hjemmesider i søgeresultater</li>
            <li>Mobiloptimering påvirker direkte din bundlinje</li>
            <li>Det er nemmere og billigere at bygge mobil-først fra starten</li>
          </ul>
        </div>
      </section>

              {/* Related Articles */}
              <section className="related-content-section">
                <h3>Læs også disse artikler</h3>
                <div className="related-articles">
                  <Link to="/artikler/webdesign-trends" className="related-article">
                    <h4>Webdesign Trends 2025</h4>
                    <p>Opdage de nyeste trends inden for webdesign og hvordan de påvirker brugeroplevelsen...</p>
                  </Link>
                  
                  <Link to="/artikler/seo-guide-2025" className="related-article">
                    <h4>Komplet SEO Guide 2025</h4>
                    <p>Alt du behøver at vide om søgemaskineoptimering i 2025. Fra grundlæggende til avancerede teknikker...</p>
                  </Link>
                  
                  <Link to="/artikler/hvad-koster-hjemmesider" className="related-article">
                    <h4>Hvad Koster en Hjemmeside?</h4>
                    <p>Få det komplette overblik over priser på hjemmesider i 2025. Fra simple sites til avancerede løsninger...</p>
                  </Link>
                  
                  <Link to="/artikler/verden-med-ai" className="related-article">
                    <h4>Verden Med AI</h4>
                    <p>Lær hvordan kunstig intelligens påvirker din online tilstedeværelse og hjemmeside performance...</p>
                  </Link>
                </div>
              </section>

            </main>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default MobilvenligeHjemmesider;